import { ChangeEvent, FC, useEffect } from 'react';

import { capitalizeFirstLetter } from '@eneco/common/string';
import { typedYupResolver } from '@eneco/hooks/form';
import { InputText, RadioButton, RadioGroup } from '@eneco/sparky';
import { Fields as CvKetelKoopFlowFields } from '@sitecore/types/CvKetelKoopFlowPersonalDetailsStep';
import { Fields as SolarBoilerLeadFlowFields } from '@sitecore/types/SolarBoilerLeadFlowPersonalDetailsStep';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { Header, Layout, NextButton } from '../../components';
import { EnecoInHuisFlowContext } from '../../types';
import { useFlowHooks } from '../../utils/FlowProvider';

interface FormFields {
  salutation: string;
  firstName: string;
  surnamePreposition: string;
  surname: string;
}

interface Props {
  textualData?: Omit<SolarBoilerLeadFlowFields, 'personalDetails'> & {
    personalDetails?: Partial<SolarBoilerLeadFlowFields['personalDetails'] & CvKetelKoopFlowFields['personalDetails']>;
  };
}

export const EnecoInHuisLeadFlowsPersonalDetailsStep: FC<Props> = ({ textualData }) => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<EnecoInHuisFlowContext>();

  const sendToFlowMachine = useFlowActorRef().send;
  const salutation = useFlowSelector(state => state.context.salutation);
  const firstName = useFlowSelector(state => state.context.firstName);
  const surname = useFlowSelector(state => state.context.surname);
  const surnamePreposition = useFlowSelector(state => state.context.surnamePreposition);

  // imposed by upstream service
  const SURNAME_PREPOSITION_MAX_LENGTH = 9;

  const salutationList =
    textualData?.personalDetails?.salutationRadioList || textualData?.personalDetails?.salutationList;

  const formSchema = yup.object({
    salutation: yup.string().required(salutationList?.value?.requiredMessage),
    firstName: yup.string().required(textualData?.personalDetails?.firstNameFormField?.value?.requiredMessage),
    surnamePreposition: yup.string().max(SURNAME_PREPOSITION_MAX_LENGTH),
    surname: yup.string().required(textualData?.personalDetails?.surnameFormField?.value?.requiredMessage),
  });

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    register,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onTouched',
    defaultValues: {
      salutation,
      firstName,
      surname,
      surnamePreposition,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          salutation: formData.salutation,
          firstName: formData.firstName,
          surnamePreposition: formData.surnamePreposition || undefined,
          surname: formData.surname,
        },
      });
    }
  }, [isSubmitSuccessful, sendToFlowMachine, getValues]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && <Header.Text>{textualData.content.text.value}</Header.Text>}
      </Header>
      <Controller
        control={control}
        name="salutation"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            label={salutationList?.value?.label ?? ''}
            name={name}
            direction="row"
            value={value}
            onValueChange={onChange}
            error={errors.salutation?.message}>
            {salutationList?.value?.options?.map(option => (
              <RadioButton value={option.name} key={option.name}>
                {option.label}
              </RadioButton>
            ))}
          </RadioGroup>
        )}
      />
      <InputText
        {...register('firstName', {
          onBlur: (event: ChangeEvent<HTMLInputElement>) => {
            setValue('firstName', capitalizeFirstLetter(event?.target?.value?.trim()));
          },
        })}
        label={textualData?.personalDetails?.firstNameFormField?.value?.label ?? ''}
        placeholder={textualData?.personalDetails?.firstNameFormField?.value?.placeholder}
        hint={textualData?.personalDetails?.firstNameFormField?.value?.hint}
        error={errors.firstName?.message}
      />
      <InputText
        {...register('surnamePreposition')}
        label={textualData?.personalDetails?.surnamePrepositionFormField?.value?.label ?? ''}
        placeholder={textualData?.personalDetails?.surnamePrepositionFormField?.value?.placeholder}
        hint={textualData?.personalDetails?.surnamePrepositionFormField?.value?.hint}
        error={errors.surnamePreposition?.message}
      />

      <InputText
        {...register('surname', {
          onBlur: (event: ChangeEvent<HTMLInputElement>) => {
            setValue('surname', capitalizeFirstLetter(event?.target?.value?.trim()));
          },
        })}
        label={textualData?.personalDetails?.surnameFormField?.value?.label ?? ''}
        placeholder={textualData?.personalDetails?.surnameFormField?.value?.placeholder}
        hint={textualData?.personalDetails?.surnameFormField?.value?.hint}
        error={errors.surname?.message}
      />
      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
